import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import {
  Bold,
  Code,
  Heading2,
  Heading3,
  ImagePlus,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Redo,
  Strikethrough,
  Table as TableIcon,
  Undo,
} from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { ReportBodyJson } from '../types/weekly-report.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business RichTextEditor の onChange が返す本文。tiptap JSON（正本）と HTML（派生）。
 * @graph-connects none
 */
export interface RichTextValue {
  bodyJson: ReportBodyJson;
  bodyHtml: string;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business RichTextEditor の props。初期本文 JSON・変更コールバック・画像アップロード関数（画像ファイルを受け取り表示 URL を返す。縮小・圧縮・API 保存は呼び出し側の責務）。
 * @graph-connects none
 */
export interface RichTextEditorProps {
  initialContent?: ReportBodyJson;
  onChange: (value: RichTextValue) => void;
  uploadImage: (file: File) => Promise<string>;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business ツールバーボタン。アクティブ状態と無効状態を共有スタイルで描画する。
 * @graph-connects none
 */
function ToolbarButton({
  onClick,
  active,
  label,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  label: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
      }}
      onClick={onClick}
      aria-label={label}
      title={label}
      aria-pressed={active ?? false}
      className={`flex h-8 w-8 items-center justify-center rounded-md border text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)] ${
        active
          ? 'border-[var(--color-accent-primary)] bg-[var(--color-accent-primary)]/10 text-[var(--color-text-primary)]'
          : 'border-transparent hover:border-[var(--color-border)]'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business ツールバー本体。tiptap editor インスタンスに対し各種書式コマンドを発行する。
 * @graph-connects none
 */
function Toolbar({ editor, onPickImage }: { editor: Editor; onPickImage: () => void }) {
  return (
    // sticky top-14: 共有 Header（h-14, sticky）の直下に張り付き、本文が長くなっても
    // スクロールに追従して書式操作できるようにする。
    <div className="sticky top-14 z-10 flex flex-wrap items-center gap-1 rounded-t-lg border-b border-[var(--color-border)] bg-[var(--color-bg-base)] px-2 py-1.5">
      <ToolbarButton
        label="太字"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="h-4 w-4" aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        label="斜体"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="h-4 w-4" aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        label="打ち消し線"
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough className="h-4 w-4" aria-hidden="true" />
      </ToolbarButton>
      <span className="mx-1 h-5 w-px bg-[var(--color-border)]" aria-hidden="true" />
      <ToolbarButton
        label="見出し（大）"
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="h-4 w-4" aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        label="見出し（小）"
        active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 className="h-4 w-4" aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        label="箇条書き"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="h-4 w-4" aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        label="番号付きリスト"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="h-4 w-4" aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        label="引用"
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Quote className="h-4 w-4" aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        label="コード"
        active={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <Code className="h-4 w-4" aria-hidden="true" />
      </ToolbarButton>
      <span className="mx-1 h-5 w-px bg-[var(--color-border)]" aria-hidden="true" />
      <ToolbarButton
        label="リンク"
        active={editor.isActive('link')}
        onClick={() => {
          const previous = (editor.getAttributes('link').href as string | undefined) ?? '';
          const url = window.prompt('リンク URL を入力（空で解除）', previous);
          if (url === null) return;
          if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
          }
          editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        }}
      >
        <LinkIcon className="h-4 w-4" aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton label="画像を挿入" onClick={onPickImage}>
        <ImagePlus className="h-4 w-4" aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        label="表を挿入"
        onClick={() =>
          editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
        }
      >
        <TableIcon className="h-4 w-4" aria-hidden="true" />
      </ToolbarButton>
      <span className="mx-1 h-5 w-px bg-[var(--color-border)]" aria-hidden="true" />
      <ToolbarButton label="元に戻す" onClick={() => editor.chain().focus().undo().run()}>
        <Undo className="h-4 w-4" aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton label="やり直す" onClick={() => editor.chain().focus().redo().run()}>
        <Redo className="h-4 w-4" aria-hidden="true" />
      </ToolbarButton>
    </div>
  );
}

/**
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 週次レポート用の Notion 風 WYSIWYG エディタ（tiptap）。見出し・リスト・引用・コード・リンク・画像・表をサポートし、変更のたびに本文 JSON と HTML を返す。画像は uploadImage prop 経由でサーバへ保存し、本文には参照 URL のみを埋め込む（本文の Firestore 1MiB 上限を画像が消費しないため、枚数の実質的な制限なし）。
 * @graph-connects onChange [calls] 本文 JSON / HTML を親へ通知
 * @graph-connects uploadImage [calls] 画像をサーバへ保存し表示 URL を取得
 */
export function RichTextEditor({ initialContent, onChange, uploadImage }: RichTextEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<Editor | null>(null);
  const [uploadingCount, setUploadingCount] = useState(0);

  // 画像（ファイル選択・クリップボード貼り付け・ドロップ共通）をサーバへアップロードし、
  // 返ってきた URL を img として挿入する。失敗はユーザーに提示して本文は変更しない。
  const insertImageFile = (file: File): void => {
    if (!file.type.startsWith('image/')) return;
    setUploadingCount((count) => count + 1);
    uploadImage(file)
      .then((src) => {
        editorRef.current?.chain().focus().setImage({ src }).run();
      })
      .catch((error: unknown) => {
        window.alert(error instanceof Error ? error.message : '画像のアップロードに失敗しました。');
      })
      .finally(() => {
        setUploadingCount((count) => count - 1);
      });
  };

  // DataTransfer（clipboard / drop）から画像ファイルだけを取り出す。
  const extractImageFiles = (data: DataTransfer | null): File[] => {
    if (!data) return [];
    return Array.from(data.items)
      .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter((file): file is File => file !== null);
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: { openOnClick: false } }),
      Image.configure({ inline: false }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: initialContent ?? '',
    editorProps: {
      attributes: {
        class:
          'report-prose min-h-[360px] max-w-none px-4 py-3 focus:outline-none text-[var(--color-text-primary)]',
      },
      handlePaste: (_view, event) => {
        const images = extractImageFiles(event.clipboardData);
        if (images.length === 0) return false;
        images.forEach(insertImageFile);
        return true;
      },
      handleDrop: (_view, event) => {
        const images = extractImageFiles(event.dataTransfer);
        if (images.length === 0) return false;
        event.preventDefault();
        images.forEach(insertImageFile);
        return true;
      },
    },
    onUpdate: ({ editor: e }) => {
      onChange({ bodyJson: e.getJSON() as ReportBodyJson, bodyHtml: e.getHTML() });
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  if (!editor) {
    return (
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] p-4 text-sm text-[var(--color-text-muted)]">
        エディタを読み込み中...
      </div>
    );
  }

  return (
    // overflow-hidden を付けるとツールバーの sticky が効かなくなる（クリップ祖先がスクロール基準になる）ため、
    // 角丸は子要素側（ツールバー rounded-t / フッター rounded-b）で揃える。
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
      <Toolbar editor={editor} onPickImage={() => fileInputRef.current?.click()} />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) insertImageFile(file);
          e.target.value = '';
        }}
      />
      <EditorContent editor={editor} />
      <p className="rounded-b-lg border-t border-[var(--color-border)] bg-[var(--color-bg-base)] px-4 py-1.5 text-xs text-[var(--color-text-muted)]">
        {uploadingCount > 0
          ? '画像をアップロード中...'
          : '画像はツールバーの挿入ボタン・クリップボード貼り付け（⌘V）・ドラッグ&ドロップで追加できます。大きい画像は自動で縮小されます。'}
      </p>
    </div>
  );
}
